import { onUnmounted, ref } from '@vue/composition-api';

export function useCrisisAssignmentBatch({ axios, crisisId, notify, onCompleted }) {
  const assigning = ref(false);
  const batchId = ref(null);
  const batchMessage = ref('');
  const batchProgress = ref(null);
  const batchStatus = ref(null);
  let pollTimer = null;

  const clearPollTimer = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const applyBatchState = (payload) => {
    batchStatus.value = payload?.status ?? (payload?.failed ? 'failed' : (payload?.finished ? 'completed' : null));
    batchMessage.value = payload?.message ?? 'Procesando asignación de instalaciones...';
    batchProgress.value = payload?.progress ?? payload?.percentage ?? null;
  };

  const isBatchFailed = (payload) => payload?.failed === true || payload?.status === 'failed';

  const isBatchFinished = (payload) => (
    payload?.finished === true
    || payload?.status === 'completed'
    || payload?.status === 'finished'
  );

  const finishBatch = async (payload) => {
    clearPollTimer();
    assigning.value = false;

    if (payload?.data) {
      onCompleted(payload.data);
      return;
    }

    const response = await axios.get(`/v1/operational-security-crisis/${crisisId.value}`);
    onCompleted(response.data?.data ?? response.data);
  };

  const pollBatch = async () => {
    try {
      const response = await axios.get(`/v1/operational-security-crisis/assignment-batches/${batchId.value}`);
      const payload = response.data?.data ?? response.data;

      applyBatchState(payload);

      if (isBatchFailed(payload)) {
        clearPollTimer();
        assigning.value = false;
        notify(payload?.message ?? 'Error asignando instalaciones', 'danger', 'AlertTriangleIcon');
        return;
      }

      if (isBatchFinished(payload)) {
        await finishBatch(payload);
      }
    } catch (error) {
      clearPollTimer();
      assigning.value = false;
      notify('Error consultando el estado de asignación', 'danger', 'AlertTriangleIcon');
    }
  };

  const startPolling = async (id) => {
    batchId.value = id;
    assigning.value = true;
    batchMessage.value = 'Iniciando asignación de instalaciones...';
    batchProgress.value = null;
    batchStatus.value = 'queued';

    clearPollTimer();
    await pollBatch();

    if (assigning.value) {
      pollTimer = setInterval(() => {
        pollBatch();
      }, 2000);
    }
  };

  const submitAssignment = async (payload) => {
    assigning.value = true;
    batchMessage.value = 'Enviando asignación de instalaciones...';
    batchProgress.value = null;
    batchStatus.value = 'submitting';

    try {
      const response = await axios.post(
        `/v1/operational-security-crisis/${crisisId.value}/units/assign-batch`,
        payload,
      );
      const data = response.data?.data ?? response.data;

      const id = data?.batch_id ?? data?.id;

      if (!id) {
        throw new Error('Missing batch_id');
      }

      await startPolling(id);
    } catch (error) {
      assigning.value = false;
      notify(
        error?.response?.data?.message ?? 'Error iniciando asignación de instalaciones',
        'danger',
        'AlertTriangleIcon',
      );
    }
  };

  const resetBatch = () => {
    clearPollTimer();
    assigning.value = false;
    batchId.value = null;
    batchMessage.value = '';
    batchProgress.value = null;
    batchStatus.value = null;
  };

  onUnmounted(() => {
    clearPollTimer();
  });

  return {
    assigning,
    batchId,
    batchMessage,
    batchProgress,
    batchStatus,
    resetBatch,
    submitAssignment,
  };
}
